import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '../api';
import {
  Badge,
  Button,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageActions,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface Deck {
  id: string;
  name: string;
  folder: string;
  coverUrl?: string | null;
}

interface CardHit {
  slug: string;
  displayName: string;
  previewUrl: string | null;
  imagen: string | null;
  descripciones: string[];
  deckId?: string;
  deckName?: string;
}

interface LinkedUser {
  id: string;
  twitchId: string;
  username: string;
  displayName: string;
  discordId: string;
  discordUsername: string;
  discordDisplayName: string;
  avatarUrl: string;
  oracleClaimsLeft: number;
  oracleClaimsAvailableNow?: number;
  oracleClaimsPendingThisStream?: number;
  oracleClaimsUsedThisStream?: number;
  oracleClaimsReservedThisStream?: number;
  oracleMaxClaimsPerStream?: number;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(200px, 0.52fr);
  gap: 1rem;
  align-items: stretch;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const DeckGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.55rem;
  margin-top: 0.35rem;
  padding: 0.1rem 0.05rem 0.2rem;
`;

const DeckTile = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.28rem;
  width: 66px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.12s ease;

  &:hover { transform: translateY(-1px); }

  .cover-wrap {
    width: 58px;
    height: 92px;
    border-radius: 10px;
    overflow: hidden;
    border: 2px solid ${(p) => (p.$active ? '#ff1493' : 'rgba(255, 20, 147, 0.22)')};
    box-shadow: ${(p) =>
      p.$active
        ? '0 0 0 1px rgba(255, 20, 147, 0.35), 0 0 16px rgba(255, 20, 147, 0.35)'
        : '0 2px 8px rgba(0, 0, 0, 0.25)'};
    background: linear-gradient(145deg, rgba(255, 20, 147, 0.12), rgba(90, 40, 140, 0.35));
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .fallback {
    font-size: 1.05rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.75);
  }

  .label {
    font-size: 0.64rem;
    line-height: 1.1;
    text-align: center;
    max-width: 66px;
    color: ${(p) => (p.$active ? '#ff8cc8' : 'rgba(255, 255, 255, 0.58)')};
    font-weight: ${(p) => (p.$active ? 600 : 500)};
  }
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.45rem;
  align-items: stretch;

  input { flex: 1; min-width: 0; }
`;

const SuggestList = styled.div`
  margin-top: 0.45rem;
  border: 1px solid rgba(255, 20, 147, 0.25);
  border-radius: 12px;
  background: rgba(12, 8, 26, 0.96);
  max-height: 240px;
  overflow: auto;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const SuggestItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.75rem;
  border: none;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child { border-bottom: none; }
  &:hover { background: rgba(255, 20, 147, 0.1); }

  img {
    width: 42px;
    height: 42px;
    object-fit: cover;
    border-radius: 8px;
    background: #1a1228;
    flex-shrink: 0;
  }

  .meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
  }

  .name {
    font-size: 0.88rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .deck-tag {
    font-size: 0.72rem;
    color: rgba(255, 140, 200, 0.85);
  }
`;

const PreviewBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-height: 380px;

  img.main {
    width: 100%;
    flex: 1;
    min-height: 320px;
    max-height: 420px;
    object-fit: contain;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255, 20, 147, 0.2);
  }

  .empty {
    flex: 1;
    min-height: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.2);
    border: 1px dashed rgba(255, 20, 147, 0.18);
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.82rem;
    text-align: center;
    padding: 1rem;
  }

  .meta {
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.8rem;
    line-height: 1.35;
    flex-shrink: 0;
  }

  .meta strong {
    display: block;
    color: #fff;
    font-size: 0.9rem;
    margin-bottom: 0.15rem;
  }
`;

const PreviewSurface = styled(Surface)`
  align-self: stretch;
  display: flex;
  flex-direction: column;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 20, 147, 0.25);
  background: rgba(255, 20, 147, 0.08);
  img { width: 32px; height: 32px; border-radius: 50%; }
  .claims {
    font-weight: 600;
    color: #ff8cc8;
    white-space: nowrap;
    text-align: right;
    font-size: 0.82rem;
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  cursor: pointer;
  input { width: 16px; height: 16px; }
`;

const OraclesPage: React.FC = () => {
  const [decks, setDecks] = useState<Deck[]>([]);
  const [deck, setDeck] = useState('');
  const [botReady, setBotReady] = useState(false);
  const [claimsEnabled, setClaimsEnabled] = useState(false);
  const [maxPerStream, setMaxPerStream] = useState(3);
  const [savingToggle, setSavingToggle] = useState(false);

  const [cardQ, setCardQ] = useState('');
  const [cardHits, setCardHits] = useState<CardHit[]>([]);
  const [selectedCard, setSelectedCard] = useState<CardHit | null>(null);

  const [userQ, setUserQ] = useState('');
  const [userHits, setUserHits] = useState<LinkedUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<LinkedUser | null>(null);
  const [isGift, setIsGift] = useState(false);

  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const activeDeck = selectedCard?.deckId || deck;

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: claimsCfg }] = await Promise.all([
          api.get('/oracles'),
          api.get('/config/oracle-claims'),
        ]);
        setDecks(data.decks || []);
        setBotReady(!!data.bot?.ready);
        setClaimsEnabled(claimsCfg.oracleClaimsEnabled === true);
        setMaxPerStream(claimsCfg.oracleMaxClaimsPerStream || 3);
      } catch (e: any) {
        setError(e?.response?.data?.message || e.message || 'Error cargando oráculos');
      }
    })();
  }, []);

  useEffect(() => {
    const q = cardQ.trim();
    if (!deck && !q) {
      setCardHits([]);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const params: Record<string, string> = { q: cardQ };
        if (deck) params.deck = deck;
        const { data } = await api.get('/oracles/cards', { params });
        setCardHits(data.cards || []);
      } catch (e: any) {
        setCardHits([]);
        if (q || deck) {
          setError(e?.response?.data?.message || e.message || 'Error buscando cartas');
        }
      }
    }, 180);
    return () => clearTimeout(t);
  }, [deck, cardQ]);

  useEffect(() => {
    if (userQ.trim().length < 1) {
      setUserHits([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/oracles/users', { params: { q: userQ } });
        setUserHits(data.users || []);
      } catch {
        setUserHits([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [userQ]);

  const canPublish = useMemo(
    () => !!activeDeck && !!selectedCard && !!selectedUser && !publishing && botReady && claimsEnabled,
    [activeDeck, selectedCard, selectedUser, publishing, botReady, claimsEnabled],
  );

  const toggleClaims = async () => {
    setSavingToggle(true);
    setError('');
    try {
      const { data } = await api.put('/config/oracle-claims', { enabled: !claimsEnabled });
      setClaimsEnabled(data.oracleClaimsEnabled === true);
      setToast(
        data.oracleClaimsEnabled
          ? `Oráculos ON — claims abiertos (máx. ${maxPerStream}/directo). Cupos reiniciados.`
          : 'Oráculos OFF — solo consultas. No se publica ni claimea.',
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudo cambiar el estado');
    } finally {
      setSavingToggle(false);
    }
  };

  const selectDeck = (id: string) => {
    setDeck((prev) => (prev === id ? '' : id));
    setSelectedCard(null);
    setCardHits([]);
  };

  const clearCardSearch = () => {
    setDeck('');
    setCardQ('');
    setSelectedCard(null);
    setCardHits([]);
  };

  const pickCard = (c: CardHit) => {
    setSelectedCard(c);
    setCardQ(c.displayName);
    setCardHits([]);
    if (c.deckId) setDeck(c.deckId);
  };

  const pickUser = (u: LinkedUser) => {
    setSelectedUser(u);
    setUserQ(u.displayName || u.username);
    setUserHits([]);
  };

  const publish = async () => {
    if (!canPublish || !selectedCard || !selectedUser || !activeDeck) return;
    setPublishing(true);
    setError('');
    try {
      const { data } = await api.post('/oracles/publish', {
        deck: activeDeck,
        cardSlug: selectedCard.slug,
        twitchUserId: selectedUser.id,
        discordUserId: selectedUser.discordId,
        isGift,
      });
      setToast(
        `Publicado: ${selectedCard.displayName} → ${selectedUser.displayName}` +
          (isGift
            ? ' (regalo)'
            : ` · quedan ${data.oracleClaimsLeft ?? '?'} · directo ${data.oracleClaimsUsedThisStream ?? '?'}/${maxPerStream}`),
      );
      if (!isGift) {
        const usedNow =
          typeof data.oracleClaimsUsedThisStream === 'number'
            ? data.oracleClaimsUsedThisStream
            : selectedUser.oracleClaimsUsedThisStream || 0;
        const pendingNow =
          typeof data.oracleClaimsPendingThisStream === 'number'
            ? data.oracleClaimsPendingThisStream
            : selectedUser.oracleClaimsPendingThisStream || 0;
        const leftNow =
          typeof data.oracleClaimsLeft === 'number'
            ? data.oracleClaimsLeft
            : selectedUser.oracleClaimsLeft;
        setSelectedUser({
          ...selectedUser,
          oracleClaimsLeft: leftNow,
          oracleClaimsAvailableNow: Math.max(0, leftNow - pendingNow),
          oracleClaimsPendingThisStream: pendingNow,
          oracleClaimsUsedThisStream: usedNow,
          oracleClaimsReservedThisStream: usedNow + pendingNow,
        });
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error al publicar');
    } finally {
      setPublishing(false);
    }
  };

  const used = selectedUser?.oracleClaimsUsedThisStream || 0;
  const pending = selectedUser?.oracleClaimsPendingThisStream || 0;
  const reserved =
    selectedUser?.oracleClaimsReservedThisStream ?? used + pending;
  const availableNow =
    selectedUser?.oracleClaimsAvailableNow ??
    Math.max(0, (selectedUser?.oracleClaimsLeft || 0) - pending);
  const showDeckTag = !deck && cardQ.trim().length > 0;

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Oráculos</Title>
          <Subtitle>
            Elegí maso, buscá carta y usuario vinculado. Sin maso seleccionado, la búsqueda recorre todos.
            {botReady ? ' · Bot online' : ' · Bot Discord offline'}
          </Subtitle>
        </PageHeaderMain>
        <PageActions>
          <Badge $active={claimsEnabled}>{claimsEnabled ? 'Claims ON' : 'Claims OFF'}</Badge>
        </PageActions>
      </PageHeader>

      {toast && <Toast onClick={() => setToast('')}>{toast}</Toast>}
      {error && (
        <Toast style={{ borderColor: 'crimson' }} onClick={() => setError('')}>
          {error}
        </Toast>
      )}

      <Surface style={{ marginBottom: '1rem' }}>
        <SurfaceHeader>
          <Toolbar style={{ flex: 1, justifyContent: 'space-between' }}>
            <div>
              <Muted style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Claims este directo (máx. {maxPerStream} por usuario)
              </Muted>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginTop: '0.35rem', color: '#e8dff0', fontSize: '0.86rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={claimsEnabled}
                  disabled={savingToggle}
                  onChange={toggleClaims}
                />
                {claimsEnabled
                  ? 'ON — se puede publicar / claimear'
                  : 'OFF — solo consultas (!oraculo). Sin publicar'}
              </label>
            </div>
            <Button type="button" $variant="ghost" disabled={savingToggle} onClick={toggleClaims}>
              {savingToggle ? '…' : claimsEnabled ? 'Apagar (!oraculooff)' : 'Encender (!oraculoon)'}
            </Button>
          </Toolbar>
        </SurfaceHeader>
      </Surface>

      <Grid>
        <Surface>
          <SurfaceBody>
            {!claimsEnabled && (
              <Muted style={{ marginBottom: '0.85rem', color: '#ff8cc8' }}>
                Claims cerrados: no se puede publicar. Abrí con el switch o !oraculoon.
              </Muted>
            )}

            <FormRow>
              <Label>Maso / oráculo</Label>
              <Muted style={{ margin: '0.2rem 0 0', fontSize: '0.78rem' }}>
                Tocá para filtrar. Sin selección → búsqueda global de cartas.
              </Muted>
              <DeckGrid>
                {decks.map((d) => (
                  <DeckTile
                    key={d.id}
                    type="button"
                    $active={deck === d.id}
                    onClick={() => selectDeck(d.id)}
                    title={d.name}
                  >
                    <div className="cover-wrap">
                      {d.coverUrl ? (
                        <img src={d.coverUrl} alt={d.name} loading="lazy" />
                      ) : (
                        <span className="fallback">{d.name.charAt(0)}</span>
                      )}
                    </div>
                    <span className="label">{d.name}</span>
                  </DeckTile>
                ))}
                {!decks.length && (
                  <Muted style={{ margin: 0, fontSize: '0.82rem' }}>Sin masos en Oraculos</Muted>
                )}
              </DeckGrid>
            </FormRow>

            <FormRow style={{ marginTop: '0.7rem' }}>
              <Label>Buscar carta</Label>
              <SearchRow>
                <Input
                  value={cardQ}
                  onChange={(e) => {
                    setCardQ(e.target.value);
                    setSelectedCard(null);
                  }}
                  placeholder={deck ? 'Cartas de este maso…' : 'Nombre de carta en todos los oráculos…'}
                  autoComplete="off"
                />
                <Button
                  type="button"
                  $variant="ghost"
                  $size="sm"
                  onClick={clearCardSearch}
                  disabled={!cardQ && !selectedCard && !deck}
                >
                  Limpiar
                </Button>
              </SearchRow>
              {!!cardHits.length && !selectedCard && (
                <SuggestList>
                  {cardHits.map((c) => (
                    <SuggestItem
                      type="button"
                      key={`${c.deckId || deck}-${c.slug}`}
                      onClick={() => pickCard(c)}
                    >
                      {c.previewUrl ? <img src={c.previewUrl} alt="" /> : <span style={{ width: 42 }} />}
                      <span className="meta">
                        <span className="name">{c.displayName}</span>
                        {showDeckTag && c.deckName && (
                          <span className="deck-tag">{c.deckName}</span>
                        )}
                      </span>
                    </SuggestItem>
                  ))}
                </SuggestList>
              )}
              {deck && !cardQ.trim() && !!cardHits.length && !selectedCard && (
                <Muted style={{ marginTop: '0.35rem', fontSize: '0.75rem' }}>
                  Mostrando cartas del maso seleccionado
                </Muted>
              )}
            </FormRow>

            <FormRow style={{ marginTop: '0.95rem' }}>
              <Label>Usuario (Twitch o Discord)</Label>
              <Input
                value={userQ}
                onChange={(e) => {
                  setUserQ(e.target.value);
                  setSelectedUser(null);
                }}
                placeholder="Buscar vinculados…"
                autoComplete="off"
              />
              {!!userHits.length && !selectedUser && (
                <SuggestList>
                  {userHits.map((u) => (
                    <SuggestItem type="button" key={u.id} onClick={() => pickUser(u)}>
                      {u.avatarUrl ? <img src={u.avatarUrl} alt="" /> : <span style={{ width: 42 }} />}
                      <span className="meta">
                        <span className="name">
                          {u.displayName}
                          <span style={{ opacity: 0.55, marginLeft: 8, fontWeight: 400 }}>
                            @{u.username}
                          </span>
                        </span>
                        <span className="deck-tag">
                          {u.oracleClaimsAvailableNow ?? u.oracleClaimsLeft} disp.
                          {' · '}
                          {u.oracleClaimsReservedThisStream ??
                            (u.oracleClaimsUsedThisStream || 0) +
                              (u.oracleClaimsPendingThisStream || 0)}
                          /{maxPerStream} cupo
                          {(u.oracleClaimsPendingThisStream || 0) > 0
                            ? ` (${u.oracleClaimsPendingThisStream} espera)`
                            : ''}
                        </span>
                      </span>
                    </SuggestItem>
                  ))}
                </SuggestList>
              )}
              {selectedUser && (
                <MemberRow>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectedUser.avatarUrl && <img src={selectedUser.avatarUrl} alt="" />}
                    <span>
                      {selectedUser.displayName}
                      <span style={{ opacity: 0.6 }}>
                        {' '}@{selectedUser.username}
                      </span>
                    </span>
                  </div>
                  <span className="claims">
                    {availableNow} disp.
                    <br />
                    {reserved}/{maxPerStream} este directo
                    {pending > 0 ? (
                      <>
                        <br />
                        {pending} en espera
                      </>
                    ) : null}
                  </span>
                </MemberRow>
              )}
            </FormRow>

            <CheckRow>
              <input
                type="checkbox"
                checked={isGift}
                onChange={(e) => setIsGift(e.target.checked)}
              />
              Es un regalo (no descontar carta ni cupo). Flujo normal: publicar con
              Regalo y luego Entregado en el monitor.
            </CheckRow>

            <div style={{ marginTop: '1.1rem' }}>
              <Button type="button" onClick={publish} disabled={!canPublish}>
                {publishing ? 'Publicando…' : 'Publicar en #oraculo'}
              </Button>
            </div>
          </SurfaceBody>
        </Surface>

        <PreviewSurface>
          <SurfaceBody style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Label>Preview</Label>
            <PreviewBox>
              {selectedCard?.previewUrl ? (
                <img className="main" src={selectedCard.previewUrl} alt={selectedCard.displayName} />
              ) : (
                <div className="empty">Seleccioná una carta para ver la imagen.</div>
              )}
              {selectedCard && (
                <div className="meta">
                  <strong>{selectedCard.displayName}</strong>
                  {selectedCard.deckName && <span>{selectedCard.deckName}</span>}
                </div>
              )}
            </PreviewBox>
          </SurfaceBody>
        </PreviewSurface>
      </Grid>
    </Page>
  );
};

export default OraclesPage;
